/**
 * threeWayMerge — Walter 2026-06-20. A diff3-style line merge for the
 * dormant-device conflict case.
 *
 * STATUS: NOT yet wired into the write path. Built + tested as a pure function
 * so Walter can review before it touches the live sync core. The intended
 * wiring: in `writeListWithConflictDetection`, when a divergence is detected
 * (cloud `theirs` ≠ `base`), run `mergeThreeWay(base, theirs, mine)`. If
 * `clean`, write `merged` and DON'T raise the conflict band (the two edits
 * didn't overlap — e.g. one device added a line at the top while the other
 * edited the bottom, or one deleted a line the other left alone). Only when
 * `clean` is false (both sides changed the SAME region differently) fall back
 * to today's snapshot-to-history + conflictPendingReview flow.
 *
 * This is the "clever merge — any obvious deletions" Walter asked for: a true
 * 3-way merge respects deletions made on either side and unions disjoint edits,
 * so most dormant-device wake-ups resolve silently instead of popping a
 * conflict.
 */

// Longest-common-subsequence match pairs between two line arrays.
// Returns [[i, j], …] with strictly increasing i and j — the lines that are
// unchanged between `a` and `b`, and where they sit on each side.
function lcsMatches(a, b) {
  const n = a.length;
  const m = b.length;
  // dp[i][j] = LCS length of a[i..] / b[j..]
  const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push([i, j]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) i++;
    else j++;
  }
  return out;
}

function linesEqual(x, y) {
  return x.length === y.length && x.every((v, k) => v === y[k]);
}

// Count non-blank lines. Blanks are deliberately excluded from every part of
// the move-dedup pass below — a list has many empty lines and they carry no
// identity, so counting them would produce nonsense.
function tally(lines) {
  const m = new Map();
  for (const l of lines) {
    if (l.trim() === '') continue;
    m.set(l, (m.get(l) || 0) + 1);
  }
  return m;
}

/**
 * dropManufacturedDuplicates — the move-aware pass. Walter 2026-08-10; see
 * docs/proposals/2026-08-10-move-aware-merge.md.
 *
 * A line-based merge sees a MOVE as "delete here + insert there". When both
 * devices move the SAME line to different places, each side's insert lands in
 * a region the other side didn't touch, so each resolves as "only one side
 * changed this" — and BOTH copies survive. The merge then reports clean, so it
 * was written silently, with no band and no history snapshot:
 *
 *     base   a b c d
 *     mine   c a b d      (moved c to the front)
 *     theirs a b d c      (moved c to the end)
 *     merged c a b d c    ← duplicated, clean=true
 *
 * Almost certainly the mechanism behind Walter's 2026-07-19 "merges producing
 * DUPLICATES" report, which we then attributed to the stale-base bug.
 *
 * The rule is deliberately narrow and provenance-based rather than clever:
 * the merged text may never contain MORE copies of a line than either side
 * actually had. Neither device asked for a third "c", so a third "c" is ours
 * to withdraw. Extras are dropped preferring copies that came from OUR side,
 * which leaves the line at the SERVER's position — Walter's call 2026-08-10
 * ("silent is good"), and consistent with the #159 theirs-first convention.
 *
 * What it deliberately does NOT do: touch `clean`/`conflicts` (a region that
 * genuinely clashed still goes to review), or reorder anything. A list where
 * both devices reordered still ends up in *a* sensible order rather than
 * necessarily either device's intended one — that's real move detection, a
 * separate round (option B in the proposal).
 */
function dropManufacturedDuplicates(out, origin, mine, theirs) {
  const outCounts = tally(out);
  const mineCounts = tally(mine);
  const theirsCounts = tally(theirs);
  const allowedFor = (t) => Math.max(mineCounts.get(t) || 0, theirsCounts.get(t) || 0);

  const excess = new Map();
  for (const [text, count] of outCounts) {
    const extra = count - allowedFor(text);
    if (extra > 0) excess.set(text, extra);
  }
  if (excess.size === 0) return out;

  const keep = new Array(out.length).fill(true);
  // Drop OUR copies first so the surviving one sits where the server put it;
  // 'base' (anchor lines both sides kept) is last-resort and should never be
  // reached, since an anchor exists on both sides and can't be in excess.
  for (const side of ['mine', 'both', 'theirs', 'base']) {
    for (let i = out.length - 1; i >= 0; i--) {
      if (!keep[i] || origin[i] !== side) continue;
      const need = excess.get(out[i]) || 0;
      if (need <= 0) continue;
      keep[i] = false;
      excess.set(out[i], need - 1);
    }
  }
  return out.filter((_, i) => keep[i]);
}

/**
 * mergeThreeWay(base, mine, theirs) → { merged, clean, conflicts }
 *   merged    — the merged text (only meaningful/safe to write when `clean`).
 *   clean     — true if every divergent region was resolvable (no region was
 *               changed differently by BOTH sides). Auto-resolvable.
 *   conflicts — count of genuinely-overlapping regions (0 when clean).
 *
 * Resolution per region (between two lines unchanged by both sides):
 *   - only `mine` changed it    → take mine
 *   - only `theirs` changed it   → take theirs
 *   - both changed it identically → take that (dedupe)
 *   - both INSERTED different NEW lines where the base had nothing (or only
 *     blank lines) → KEEP BOTH, theirs first (≈chronological), clean. Walter
 *     2026-07-19, reversing his 2026-06-22 "same-position inserts = conflict"
 *     call after real-world use: two different new to-do entries are both
 *     wanted — a band there is noise. If one side's insert contains the
 *     other's lines plus more, the superset wins (no duplicates); a messy
 *     partial overlap still conflicts.
 *   - both EDITED the same existing region differently → CONFLICT
 *     (clean=false). The merged text keeps both halves (theirs then mine) so
 *     nothing is lost, but callers should NOT auto-write a non-clean merge —
 *     fall back to the review flow.
 */
export function mergeThreeWay(baseStr, mineStr, theirsStr) {
  const base = String(baseStr ?? '').split('\n');
  const mine = String(mineStr ?? '').split('\n');
  const theirs = String(theirsStr ?? '').split('\n');

  const b2mine = new Map(lcsMatches(base, mine));
  const b2theirs = new Map(lcsMatches(base, theirs));

  // Anchors = base lines unchanged by BOTH sides, kept to a jointly-monotonic
  // chain (positions increasing on base, mine, and theirs together).
  const anchors = [];
  let lastMine = -1;
  let lastTheirs = -1;
  for (let bi = 0; bi < base.length; bi++) {
    if (b2mine.has(bi) && b2theirs.has(bi)) {
      const mi = b2mine.get(bi);
      const ti = b2theirs.get(bi);
      if (mi > lastMine && ti > lastTheirs) {
        anchors.push([bi, mi, ti]);
        lastMine = mi;
        lastTheirs = ti;
      }
    }
  }

  const out = [];
  // Provenance per emitted line, used only by dropManufacturedDuplicates so it
  // can drop OUR copy of a moved line and leave the server's position standing.
  const origin = [];
  const emit = (from, lines) => {
    for (const l of lines) { out.push(l); origin.push(from); }
  };
  let conflicts = 0;
  let pb = 0;
  let pm = 0;
  let pt = 0;

  // Is `small` a line-wise subsequence of `big`? Used to dedupe insert
  // collisions where one side typed the same new line(s) plus more.
  const isSubseq = (small, big) => {
    let i = 0;
    for (const l of big) { if (i < small.length && small[i] === l) i++; }
    return i === small.length;
  };

  const resolveRegion = (bi, mi, ti) => {
    const baseChunk = base.slice(pb, bi);
    const mineChunk = mine.slice(pm, mi);
    const theirsChunk = theirs.slice(pt, ti);
    if (linesEqual(mineChunk, baseChunk)) emit('theirs', theirsChunk);       // only theirs changed
    else if (linesEqual(theirsChunk, baseChunk)) emit('mine', mineChunk);    // only mine changed
    else if (linesEqual(mineChunk, theirsChunk)) emit('both', mineChunk);    // same change both sides
    else if (baseChunk.every(l => l === '')) {
      // Pure INSERT collision — both sides added new lines where the base had
      // nothing (or only blank lines). Walter 2026-07-19: keep both, no band.
      if (isSubseq(mineChunk, theirsChunk)) emit('theirs', theirsChunk);      // theirs ⊇ mine — superset wins
      else if (isSubseq(theirsChunk, mineChunk)) emit('mine', mineChunk);     // mine ⊇ theirs
      else if (mineChunk.some(l => l.trim() !== '' && theirsChunk.includes(l))) {
        conflicts++; emit('theirs', theirsChunk); emit('mine', mineChunk);    // messy partial overlap → review
      } else { emit('theirs', theirsChunk); emit('mine', mineChunk); }        // disjoint inserts: theirs (server) first
    }
    else { conflicts++; emit('theirs', theirsChunk); emit('mine', mineChunk); } // both edited same region → true conflict
  };

  for (const [bi, mi, ti] of anchors) {
    resolveRegion(bi, mi, ti);
    emit('base', [base[bi]]); // the unchanged anchor line
    pb = bi + 1; pm = mi + 1; pt = ti + 1;
  }
  resolveRegion(base.length, mine.length, theirs.length); // trailing region

  // Move-aware pass: a move is delete-here + insert-there to a line diff, so
  // two sides moving the SAME line both keep a copy. Withdraw the copies
  // neither side asked for. See dropManufacturedDuplicates.
  const deduped = dropManufacturedDuplicates(out, origin, mine, theirs);

  return { merged: deduped.join('\n'), clean: conflicts === 0, conflicts };
}
